import { useState, useEffect, useCallback } from 'react';
import { Loader2, Save, RotateCcw, Layers, Compass, Info } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { toast } from 'sonner';
import api from '../../lib/api';
import { cn } from '../../lib/utils';

// Editable table that lets an org tune which Solution-Finder interventions
// each skill family / skill type maps to — the map that powers the
// Capability Inventory → Solution Finder "close this gap" auto-select.
export default function CapabilityInterventionMapEditor({ selectedOrg = '' }) {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [data, setData] = useState(null);
  // { family: {name: [ids]}, type: {technical: [ids], durable: [ids]} }
  const [draft, setDraft] = useState({ family: {}, type: {} });

  const orgParam = selectedOrg ? `?organization_id=${selectedOrg}` : '';

  const hydrate = useCallback((d) => {
    const family = {};
    (d.families || []).forEach((f) => { family[f] = [...(d.resolved.family[f] || [])]; });
    const type = {};
    (d.types || []).forEach((t) => { type[t] = [...(d.resolved.type[t] || [])]; });
    setDraft({ family, type });
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get(`/capability-inventory/intervention-map${orgParam}`);
      setData(res.data);
      hydrate(res.data);
    } catch {
      toast.error('Could not load the skill → intervention map');
    } finally {
      setLoading(false);
    }
  }, [orgParam, hydrate]);

  useEffect(() => { load(); }, [load]);

  const toggle = (bucket, key, id) => {
    setDraft((prev) => {
      const current = prev[bucket][key] || [];
      const next = current.includes(id) ? current.filter((x) => x !== id) : [...current, id];
      return { ...prev, [bucket]: { ...prev[bucket], [key]: next } };
    });
  };

  const save = async () => {
    setSaving(true);
    try {
      const rows = [];
      Object.entries(draft.family).forEach(([k, ids]) => {
        if (ids.length) rows.push({ key_type: 'family', key_value: k, intervention_ids: ids });
      });
      Object.entries(draft.type).forEach(([k, ids]) => {
        rows.push({ key_type: 'type', key_value: k, intervention_ids: ids });
      });
      const res = await api.put('/capability-inventory/intervention-map', {
        organization_id: selectedOrg || undefined,
        rows,
      });
      toast.success(`Saved — ${res.data.count} mapping rule${res.data.count !== 1 ? 's' : ''} active`);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const reset = async () => {
    setSaving(true);
    try {
      await api.post('/capability-inventory/intervention-map/reset', {
        organization_id: selectedOrg || undefined,
      });
      toast.success('Reverted to system defaults');
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Reset failed');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center py-16" data-testid="cap-map-loading">
        <Loader2 className="h-6 w-6 animate-spin text-teal-600" />
      </div>
    );
  }
  if (!data) return null;

  const canEdit = data.can_edit;
  const catalog = data.available_interventions || [];

  const renderRow = (bucket, key, label, sublabel) => {
    const selected = draft[bucket][key] || [];
    return (
      <div
        key={`${bucket}-${key}`}
        className="rounded-lg border border-slate-200 p-3"
        data-testid={`cap-map-row-${bucket}-${key.replace(/\s+/g, '_')}`}
      >
        <div className="flex items-center justify-between mb-2">
          <div className="min-w-0">
            <p className="text-sm font-semibold text-slate-800 truncate">{label}</p>
            {sublabel && <p className="text-[11px] text-slate-400">{sublabel}</p>}
          </div>
          <Badge variant="outline" className="text-[10px] shrink-0 bg-slate-50 text-slate-600">
            {selected.length} selected
          </Badge>
        </div>
        <div className="flex flex-wrap gap-1.5">
          {catalog.map((iv) => {
            const on = selected.includes(iv.id);
            return (
              <button
                key={iv.id}
                type="button"
                disabled={!canEdit}
                onClick={() => toggle(bucket, key, iv.id)}
                className={cn(
                  'text-xs px-2.5 py-1 rounded-full border transition-colors disabled:opacity-60 disabled:cursor-not-allowed',
                  on
                    ? 'bg-cyan-600 text-white border-cyan-600'
                    : 'bg-white text-slate-600 border-slate-300 hover:border-cyan-400',
                )}
                data-testid={`cap-map-chip-${bucket}-${key.replace(/\s+/g, '_')}-${iv.id}`}
                title={iv.category}
              >
                {iv.name}
              </button>
            );
          })}
        </div>
      </div>
    );
  };

  return (
    <div className="space-y-4" data-testid="cap-intervention-map-editor">
      <Card className="border-cyan-200 bg-gradient-to-br from-cyan-50/50 to-white">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-cyan-800 text-base">
            <Compass className="h-5 w-5" />
            Skill → Intervention Map
          </CardTitle>
          <CardDescription>
            Tune which learning interventions the Capability Inventory recommends when a
            member has a skill gap. A <strong>family</strong> rule wins over a{' '}
            <strong>type</strong> rule. Leave a family empty to fall back to its skill-type
            rule. Powers the one-click "Find interventions" bridge into Solution Finder.
          </CardDescription>
        </CardHeader>
        <CardContent className="flex items-center gap-2">
          <Button
            size="sm"
            className="bg-cyan-600 hover:bg-cyan-700 gap-1.5"
            onClick={save}
            disabled={!canEdit || saving}
            data-testid="cap-map-save-btn"
          >
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
            Save mapping
          </Button>
          <Button
            size="sm"
            variant="outline"
            className="gap-1.5"
            onClick={reset}
            disabled={!canEdit || saving}
            data-testid="cap-map-reset-btn"
          >
            <RotateCcw className="h-4 w-4" />
            Reset to defaults
          </Button>
          {!canEdit && (
            <span className="text-xs text-slate-500 flex items-center gap-1">
              <Info className="h-3.5 w-3.5" /> Read-only — org admin required to edit
            </span>
          )}
        </CardContent>
      </Card>

      <div>
        <h4 className="text-sm font-semibold text-slate-700 mb-2 flex items-center gap-1.5">
          <Layers className="h-4 w-4 text-slate-400" /> By Skill Family
        </h4>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3" data-testid="cap-map-family-rows">
          {(data.families || []).map((f) => renderRow('family', f, f, 'Family-level rule (highest priority)'))}
        </div>
      </div>

      <div>
        <h4 className="text-sm font-semibold text-slate-700 mb-2 flex items-center gap-1.5">
          <Layers className="h-4 w-4 text-slate-400" /> By Skill Type (fallback)
        </h4>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3" data-testid="cap-map-type-rows">
          {(data.types || []).map((t) => renderRow('type', t, t.charAt(0).toUpperCase() + t.slice(1), 'Applies when no family rule matches'))}
        </div>
      </div>
    </div>
  );
}
